/** @jsxImportSource react */
'use client';

import { motion } from 'motion/react';

import { Dove } from './brand';
import { ArrowRight } from './icons';
import { SignupLink } from './signup-link';
import { TrainingLink } from './training-link';

// Word-by-word stagger config — Mercury-style: slow, restrained.
const HEADLINE_LINES = [
  ["Don't", 'let', 'guests'],
  ['slip', 'through'],
  [' the', 'cracks.'],
];

export function Hero() {
  return (
    <section className="relative overflow-hidden bg-white">
      {/* Dove watermark — slow continuous drift gives the page subtle life */}
      <motion.div
        animate={{ rotate: [0, 1.5, 0, -1.5, 0], y: [0, -8, 0, 8, 0] }}
        className="pointer-events-none absolute -top-[100px] -right-[160px] z-0"
        transition={{ duration: 24, ease: 'easeInOut', repeat: Infinity }}
      >
        <Dove aria-hidden className="h-[780px] w-[900px] opacity-[0.06]" />
      </motion.div>

      <div className="relative z-10 mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-5 pt-16 pb-20 sm:px-8 sm:pt-20 sm:pb-24 lg:grid-cols-[1fr_480px] lg:items-end lg:gap-20 lg:px-20 lg:pt-[120px] lg:pb-[100px]">
        {/* Left — Headline */}
        <div className="flex max-w-[820px] flex-col gap-10">
          <div className="flex items-center gap-3">
            <div className="h-px w-8 bg-[color:var(--color-amethyst)]" />
            <span className="text-[13px] font-medium uppercase tracking-[0.12em] text-[color:var(--color-amethyst)]">
              Church class management — your classes online 24/7
            </span>
          </div>

          {/* Headline rendered statically. The original per-word
              motion.spans were leaving words stuck at opacity:0 on
              first paint (motion v12 + React 19 strict mode
              regression). The outermost motion.div on the column
              wrapper still fades the whole hero in subtly, which is
              enough — losing the per-word reveal is not worth risking
              an invisible headline. */}
          <h1 className="font-bold text-[color:var(--color-ink)] [letter-spacing:-0.035em] [line-height:1.05]">
            {HEADLINE_LINES.map((line, li) => (
              <span key={li} className="block text-[clamp(40px,8vw,96px)]">
                {line.join(' ')}
              </span>
            ))}
          </h1>

          <div className="flex max-w-[620px] flex-col gap-4">
            <p className="text-[20px] leading-[1.55] text-[color:var(--color-ink)]">
              OnArk is the class management system for churches. Put your growth
              track, membership class, baptism class, and volunteer onboarding
              online — they run 24/7, self-paced, with DISC, Spiritual Gifts,
              and Planning Center sync built in.
            </p>
            <p className="text-[18px] leading-[1.6] text-[color:var(--color-gray-dark)]">
              Serve teams that grow. Pastors who get their evenings back. Guests
              who finish a real next step.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-5 pt-2">
            <SignupLink
              className="group inline-flex items-center gap-2.5 rounded-[12px] bg-[color:var(--color-amethyst)] px-9 py-[18px] text-[17px] font-semibold text-white shadow-[0_12px_32px_-8px_rgba(123,44,255,0.35)] transition-all hover:shadow-[0_18px_42px_-10px_rgba(123,44,255,0.5)] hover:-translate-y-0.5"
              source="hero"
            >
              <span>Try OnArk free</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </SignupLink>
            <TrainingLink
              className="link-underline text-[16px] font-medium text-[color:var(--color-ink)] transition-colors hover:text-[color:var(--color-amethyst)]"
              source="home_hero"
            >
              Or see how it works →
            </TrainingLink>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-[13px] font-medium uppercase tracking-[0.06em] text-[color:var(--color-gray-med)]">
              Free 21-day trial
            </span>
            <span className="text-[color:var(--color-gray-light)]">·</span>
            <span className="text-[13px] font-medium uppercase tracking-[0.06em] text-[color:var(--color-gray-med)]">
              Card up front, $0 today
            </span>
            <span className="text-[color:var(--color-gray-light)]">·</span>
            <span className="text-[13px] font-medium uppercase tracking-[0.06em] text-[color:var(--color-gray-med)]">
              Planning Center native
            </span>
          </div>
        </div>

        {/* Right — Customer quote card */}
        <div className="flex flex-col gap-5">
          <motion.div
            className="rounded-[20px] border-l-4 border-[color:var(--color-amethyst)] bg-[color:var(--color-bg-mist)] p-9 transition-shadow hover:shadow-[0_24px_60px_-20px_rgba(123,44,255,0.18)]"
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -4 }}
          >
            <svg
              className="mb-5 opacity-30"
              fill="none"
              height="32"
              viewBox="0 0 32 32"
              width="32"
            >
              <path
                d="M8 22 Q8 14 14 11 L14 14 Q11 16 11 20 L13 20 L13 24 L8 24 Z M20 22 Q20 14 26 11 L26 14 Q23 16 23 20 L25 20 L25 24 L20 24 Z"
                fill="var(--color-amethyst)"
              />
            </svg>
            <p className="text-[24px] font-medium leading-[1.42] tracking-[-0.01em] text-[color:var(--color-ink)]">
              We were growing but seeing people falling through the cracks. We
              moved it online and saw our serve team{' '}
              <span className="highlight-block">double in a few months.</span>
            </p>
            <div className="mt-6 flex items-center gap-3 border-t border-[#E2D6FF] pt-5">
              {/* Customer headshot slot. Drop a square image at
                  /illustrations/customers/bobby-mylife.jpg (recommended 96x96, 2x retina),
                  then swap the gradient-initials block below for an <Image> tag. */}
              <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-[color:var(--color-amethyst)] to-[color:var(--color-amethyst-dark)] text-[14px] font-bold text-white">
                BB
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[14px] font-medium text-[color:var(--color-ink)]">
                  Bobby
                </span>
                <span className="text-[12px] text-[color:var(--color-gray-dark)]">
                  Discipleship/Next Steps · MyLife Church · 250–500 weekly
                </span>
              </div>
            </div>
          </motion.div>
          <div className="flex items-center gap-2 pl-2">
            <div className="h-1.5 w-1.5 rounded-full bg-[color:var(--color-highlight)]" />
            <span className="text-[13px] text-[color:var(--color-gray-med)]">
              From our 2026 customer survey.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
